import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "https://loompdf.com";
const DEFAULT_DESC =
  "Loom turns long PDFs into horizontal study decks — walk through AI-made sections and quiz yourself on what matters.";

// Per-route unique title + description. Keeps titles/descriptions distinct so
// search engines don't see duplicates across pages.
const ROUTES = {
  "/": {
    title: "Loom — Turn long PDFs into horizontal study decks",
    description:
      "Upload any PDF and Loom breaks it into vivid, walk-through study sections with AI summaries and auto-generated quizzes. Study smarter, remember longer.",
  },
  "/pricing": {
    title: "Pricing — Loom study plans (Free, Pro & Lifetime)",
    description:
      "Simple plans for students and candidates: start free, or unlock unlimited decks, full quizzes and PDF/PPT exports with Pro Monthly, Pro Annual or Lifetime.",
  },
  "/register": {
    title: "Create your free Loom account",
    description:
      "Sign up free to turn your PDFs into horizontal study decks with AI sections and quizzes. No card required to start.",
  },
  "/login": {
    title: "Sign in to Loom",
    description: "Log in to your Loom account to access your study decks, quizzes and exports.",
  },
  "/forgot-password": {
    title: "Reset your Loom password",
    description: "Request a secure link to reset the password on your Loom account.",
  },
  "/reset-password": {
    title: "Choose a new Loom password",
    description: "Set a new password for your Loom account.",
    noindex: true,
  },
  "/terms": {
    title: "Terms & Conditions — Loom",
    description: "The terms that govern your use of Loom, including accounts, subscriptions and acceptable use.",
  },
  "/privacy": {
    title: "Privacy Policy — Loom",
    description: "How Loom collects, uses and protects your data, including documents you upload and payment metadata.",
  },
  "/refund": {
    title: "Refund & Cancellation Policy — Loom",
    description: "Loom's refund and cancellation policy for Pro Monthly, Pro Annual and Lifetime plans.",
  },
  "/contact": {
    title: "Contact Loom",
    description: "Get in touch with the Loom team for help with your account, billing or study decks.",
  },
  "/tools/pdf-to-flashcards": {
    title: "Free PDF to Flashcards Converter Online",
    description:
      "Convert any PDF into study flashcards free and online. Loom turns textbooks, lecture notes and revision guides into AI-generated flashcards, MCQs and recall prompts.",
  },
  "/tools/textbook-summarizer": {
    title: "AI Textbook Summarizer for Students",
    description:
      "Loom's AI textbook summarizer turns dense chapters into clear horizontal review slides with summaries, key points and quizzes — revise a whole chapter in one sitting.",
  },
  "/use-cases/medical-students": {
    title: "Study Decks for Medical and Nursing Students",
    description:
      "Turn heavy medical and nursing PDFs — textbooks, lectures and guidelines — into horizontal study decks with AI summaries and high-yield flashcards and quizzes.",
  },
};

// Authenticated / non-indexable areas → noindex, no canonical.
const PRIVATE_PREFIXES = ["/dashboard", "/deck", "/quiz", "/circle", "/admin", "/billing"];

export default function Seo() {
  const { pathname } = useLocation();
  const isPrivate = PRIVATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  const meta = ROUTES[pathname] || {};
  const title = meta.title || "Loom — Turn long PDFs into horizontal study decks";
  const description = meta.description || DEFAULT_DESC;
  const noindex = isPrivate || meta.noindex;
  const canonical = `${SITE}${pathname === "/" ? "/" : pathname}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      {!noindex && <link rel="canonical" href={canonical} />}
      {/* Open Graph / social */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Loom" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {!noindex && <meta property="og:url" content={canonical} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}
